import { Checkbox, Divider, Group, Stack, Text } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import dayjs from 'dayjs';
import { Button } from '../../../../components/button/button';
import { useFinancialsStore } from '../../../../store/financials-store/financials-store';
import {
  setCostVersusOutputMeasures,
  setCostVersusOutputTrendLines,
} from '../../../../store/financials-store/financials-store.actions';
import type { CostVersusOutputMeasures } from '../../../../store/financials-store/financials-store.type';
import { useGlobalStore } from '../../../../store/global-store/global-store';
import { newCOLORS } from '../../../../styles/colors';
import { Icon } from '../../../../ui-library/icon/icon';
import { useCostVersusOutput } from '../../financials.hooks';
import { buildAverageValueLabel, measureColorMap, measureNameMap } from './cost-versus-output.helpers';

function Filters({ setShow }: { setShow: (show: boolean) => void }) {
  const [isMeasuresOpen, { toggle: toggleMeasures }] = useDisclosure(true);
  const [isTrendsOpen, { toggle: toggleTrends }] = useDisclosure(true);
  const {
    filters: { costVersusOutput },
  } = useFinancialsStore();

  const portfolio = useGlobalStore((state) => state.portfolio);
  const teamId = useFinancialsStore((state) => state.team);
  const boardId = useFinancialsStore((state) => state.board);
  const startDate = useFinancialsStore((state) => state.startDate);
  const endDate = useFinancialsStore((state) => state.endDate);

  const { data } = useCostVersusOutput(
    dayjs(startDate).toDate(),
    dayjs(endDate).toDate(),
    portfolio?.id || null,
    teamId,
    boardId,
  );

  return (
    <Stack>
      <Group justify="space-between">
        <Text fw={750}>Filters</Text>
        <Button variant="outline" size="xs" onClick={() => setShow(false)}>
          <Icon name="keyboard_arrow_left" style={{ cursor: 'pointer' }} />
        </Button>
      </Group>
      <Divider />
      <Group justify="space-between">
        <Group>
          <Icon name="adjust" size={24} color={newCOLORS.gray} />
          <Text fw={750}>Measures</Text>
        </Group>
        <Icon
          name={isMeasuresOpen ? 'unfold_less' : 'unfold_more'}
          size={24}
          color={newCOLORS.gray}
          onClick={toggleMeasures}
          style={{ cursor: 'pointer' }}
        />
      </Group>
      <Divider />
      {isMeasuresOpen && (
        <Checkbox.Group
          value={costVersusOutput.selectedMeasures}
          onChange={(value) => setCostVersusOutputMeasures(value as CostVersusOutputMeasures[])}
        >
          <Stack gap={16}>
            {Object.entries(measureNameMap).map(([key, label]) => (
              <FilterItem
                key={key}
                label={label}
                value={key}
                color={measureColorMap[key as keyof typeof measureColorMap]}
                amountLabel={buildAverageValueLabel(key as keyof typeof measureNameMap, data)}
              />
            ))}
          </Stack>
        </Checkbox.Group>
      )}
      {costVersusOutput.selectedMeasures.length > 0 && (
        <>
          <Divider />
          <Group justify="space-between">
            <Group>
              <Icon name="filter_tilt_shift" size={24} color={newCOLORS.gray} />
              <Text fw={750}>Trends</Text>
            </Group>
            <Icon
              name={isTrendsOpen ? 'unfold_less' : 'unfold_more'}
              size={24}
              color={newCOLORS.gray}
              onClick={toggleTrends}
              style={{ cursor: 'pointer' }}
            />
          </Group>
          <Divider />
          {isTrendsOpen && (
            <Checkbox.Group
              value={costVersusOutput.selectedTrendLines}
              onChange={(value) => setCostVersusOutputTrendLines(value as CostVersusOutputMeasures[])}
            >
              <Stack gap={16}>
                {Object.entries(measureNameMap)
                  .filter(([key, _]) => costVersusOutput.selectedMeasures.includes(key as keyof typeof measureNameMap))
                  .map(([key, label]) => (
                    <FilterItem
                      key={key}
                      label={label}
                      value={key}
                      color={measureColorMap[key as keyof typeof measureColorMap]}
                    />
                  ))}
              </Stack>
            </Checkbox.Group>
          )}
        </>
      )}
    </Stack>
  );
}

function FilterItem({
  label,
  value,
  color,
  amountLabel,
  description,
}: { label: string; value: string; color: string; amountLabel?: string; description?: string }) {
  return (
    <Stack gap={4}>
      <Group justify="space-between">
        <Group>
          <Checkbox color={color} value={value} styles={{ input: { cursor: 'pointer' } }} />
          <Text size="md" fw={750}>
            {label}
          </Text>
        </Group>
        <Text size="xs" c="gray">
          {amountLabel}
        </Text>
      </Group>
      {description && <Text c="black">{description}</Text>}
    </Stack>
  );
}

export { Filters };
